import { useEffect, useState } from 'react';
import EditButton from '../../components/EditButton/EditButton';
import { Avatar, Box, Button, Card, Divider, Drawer, Grid, IconButton, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, Typography, styled, tableCellClasses } from '@mui/material';
import { useParams } from 'react-router-dom';
import BreadCrumb from "../../components/Breadcrumbs/Breadcrumbs";
import LegalInformation from "../../components/LegalInformation/LegalInformation";
import AccountingInformation from "../../components/AccountingInformation/AccountingInformation";
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import AccountBasicInfo from '../../components/AccountBasicInfo/AccountBasicInfo';
import AccountBankInfo from '../../components/AccountBankInfo/AccountBankInfo';
import AccountAddressInfo from '../../components/AccountAddressInfo/AccountAddressInfo';
import AccountDocumentSettingInfo from '../../components/AccountDocumentSettingsInfo/AccountDocumentSettingsInfo';
import Subscription from '../../components/Subscription/Subscription';
import { fetchCountries } from '../../store/countryStore/countryThunk';
import { TabPanel } from '../../components/Dialog/TabPanel';
import { fetchAccountById, updateAccount, removeAccountLogoOrSign } from "../../store/accountThunk";
import styled_ from "@emotion/styled";
import moment from 'moment';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CustomDialogForm from '../../components/Dialog/DialogForm';


interface StyledTabsProps {
  children?: React.ReactNode;
  value: number;
  onChange: (event: React.SyntheticEvent, newValue: number) => void;
}

  const StyledTabs = styled((props: StyledTabsProps) => (
    <Tabs
      {...props}
    />
  ))({
    backgroundColor: 'transparent',
    padding: '0px',
    minHeight: '40px',
    maxWidth: 'fit-content',
    '& .MuiTabs-flexContainer': {
      gap: '4px',
    },
    '& .MuiTabs-indicator': {
      display: 'none',
    },
    '& .MuiTab-root': {
      textTransform: 'none',
      fontSize: '14px',
      fontWeight: 500,
      color: '#4b5563',
      zIndex: 2,
      minWidth: 'auto',
      height: '36px',
      minHeight: '36px',
      padding: '8px 20px',
      borderRadius: '8px',
      transition: 'all 0.2s ease-in-out',
      '&:hover:not(.Mui-selected)': {
        backgroundColor: '#f3f4f6',
        color: '#047857',
      },
      '&.Mui-selected': {
        backgroundColor: '#059669',
        color: '#ffffff',
        fontWeight: 600,
        boxShadow: '0 2px 8px rgba(5, 150, 105, 0.3)',
      },
    },
  });

interface TabPanelProps {
  index: number;
  value: number;
}

const StyledTabPanel = styled((props: TabPanelProps) => (
  <TabPanel
    {...props}
  />
))({
  // Only reset TabPanel's own wrapper; a descendant selector would also strip padding from boxes inside the cards
  '& > .MuiBox-root': {
    backgroundColor: 'transparent !important',
    padding: '0px !important',
    margin: '0 !important',
    minHeight: 'auto !important',
  }
});

interface StyledTabProps {
  label: string;
}

const StyledTab = styled((props: StyledTabProps) => (
  <Tab disableRipple {...props} />
))(({ theme }) => ({
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.pxToRem(15),
  marginRight: theme.spacing(1),
  color: '#545454',
  '&.Mui-selected': {
    color: '#059669',
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'rgba(100, 95, 228, 0.32)',
  },
}));

const getInitials = (name = '') =>
  name.split(' ').filter(Boolean).slice(0, 2).map(part => part[0].toUpperCase()).join('');

/**
 * Component to show account info page.
 */
const AccountInfo = () => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const [logoUrl, setLogoUrl] = useState('')
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const accountDetails = useAppSelector(state => state.account.accountDetails);

  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (params.accountId) {
      dispatch(fetchAccountById(params.accountId));
    }
    dispatch(fetchCountries());
  }, [params, dispatch]);

  useEffect(() => {
    let basicInfoAvaliable = true;

    const basicInfo = () => {
      try {

        if (basicInfoAvaliable) {
          setLogoUrl(accountDetails.logo)
        }
      } catch (error: any) {
        console.log('Company Profile catch block called')
      }
    };
    basicInfo();
    return () => {
      basicInfoAvaliable = false;
    };
  }, [accountDetails]);

  const handleRemoveLogoOrSign = async (logoOrSign: any) => {
    let data: any = {};
    if (logoOrSign == 'logo') {
      data.logo = true;
    }
    if (logoOrSign == 'signature') {
      data.signature = true;
    }

    console.log(data);

    try {
      await dispatch(removeAccountLogoOrSign({
        id: accountDetails._id,
        accountPayload: data,
      })).then((data) => {
        if (data) {
          dispatch(fetchAccountById(accountDetails._id));
          handleClose();
        }
      }).catch(e => {
        //console.log(e);
      });;
    } catch (error: any) {
      // Empty catch block.
    }
  }

  const onSubmit = async (data: any) => {
    // Dispatch add account action.
    try {
      await dispatch(updateAccount({
        id: accountDetails._id,
        accountPayload: data,
      })).then((data) => {
        if (data) {
          dispatch(fetchAccountById(accountDetails._id));
          handleClose();
        }
      }).catch(e => {
        //console.log(e);
      });;
    } catch (error: any) {
      // Empty catch block.
    }
  };


  // Some accounts store the literal string "undefined" for empty fields
  const clean = (value?: string) => (value && value !== 'undefined' ? value : '');
  const businessType = clean(accountDetails?.businessType);
  const industryVertical = clean(accountDetails?.industryVertical);

  return (
    <>
      <Card sx={{ mt: 1, mb: 3, mr: 2, borderRadius: '8px', boxShadow: 'none', border: '1px solid #dcdcdc', bgcolor: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2.5, flexWrap: 'wrap' }}>
          <Avatar
            alt={accountDetails?.displayName}
            imgProps={{ "referrerPolicy": 'no-referrer' }}
            src={logoUrl || undefined}
            variant='rounded'
            sx={{ width: 64, height: 64, fontSize: '24px', borderRadius: '12px', bgcolor: 'primary.main', border: '1px solid #e4e4e4', '& img': { objectFit: 'contain', bgcolor: '#fff', p: 0.5 } }}
          >
            {getInitials(accountDetails?.displayName)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '20px', color: '#1a1a1a' }}>{accountDetails?.displayName || '-'}</Typography>
            </Box>
            <Typography sx={{ color: '#5f6368', fontSize: '14px' }}>
              {[accountDetails?.legalName !== accountDetails?.displayName ? clean(accountDetails?.legalName) : '', businessType, industryVertical].filter(Boolean).join('  •  ') || 'My Business Information'}
            </Typography>
          </Box>
          {accountDetails?._id && (
            <EditButton onClick={handleOpen} />
          )}
        </Box>

        <Box sx={{ px: 3, py: 0.75, bgcolor: '#fff', borderTop: '1px solid #ececec', borderBottom: '1px solid #dcdcdc', overflowX: 'auto' }}>
          <StyledTabs value={value}
            onChange={handleChange}
            aria-label="account info tabs">
            <StyledTab label="General" />
            <StyledTab label="Banks" />
            <StyledTab label="Addresses" />
            <StyledTab label="Document Settings" />
            <StyledTab label="Subscription" />
          </StyledTabs>
        </Box>

        <Box sx={{ px: 3, py: 2.5, bgcolor: '#fff', '& .c-card2': { bgcolor: '#fff', border: '1px solid #dcdcdc', borderRadius: '8px', boxShadow: 'none', mb: 2, cursor: 'default' },
          // Invoice.css turns any hovered element's border blue inside .c-card2; keep the card header line grey
          '& .c-card2 :hover:not(.MuiButton-root)': { borderColor: '#dcdcdc' } }}>
          <StyledTabPanel value={value} index={0} >
            <Grid container spacing={2} >
              <Grid item xs={12} lg={8}>
                <AccountBasicInfo />
                <AccountingInformation />
              </Grid>
              <Grid item xs={12} lg={4}>
                <LegalInformation />
              </Grid>
            </Grid>
          </StyledTabPanel>
          <StyledTabPanel value={value} index={1} >
            <AccountBankInfo accountId={params.accountId || ''} />
          </StyledTabPanel>
          <StyledTabPanel value={value} index={2} >
            <AccountAddressInfo accountId={params.accountId || ''} />
          </StyledTabPanel>
          <StyledTabPanel value={value} index={3} >
            <AccountDocumentSettingInfo accountId={params.accountId || ''} />
          </StyledTabPanel>
          <StyledTabPanel value={value} index={4} >
            <Subscription />
          </StyledTabPanel>
        </Box>
      </Card>

      <Drawer
        anchor="right"
        open={open}
      >
        {<CustomDialogForm title='Update Account' open={open} data={accountDetails} onClose={handleClose} onSubmit={onSubmit} />}
      </Drawer>
    </>
  );
}

export default AccountInfo;
